import { useState, type ReactElement } from 'react';
import { ActionIcon, Badge, Box, Card, Group, Paper, Stack, Switch, Text, TextInput, Tooltip } from '@mantine/core';
import { CornerUpLeft, Minus, Plus, Sparkles, X } from 'lucide-react';
import type { UsageCategoriesViewModel } from './UsageCategoriesViewModel';
import type { TaxonomyDraftNode } from './UsageCategoryTaxonomyTypes';
import { canAddBeneathDepth } from './UsageCategoryTaxonomyTypes';

export default function TaxonomyChart({ vm, onAssist }: { vm: UsageCategoriesViewModel; onAssist: (draftId: string) => void }): ReactElement {
    const [dragId, setDragId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [rootOver, setRootOver] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');

    const beginEdit = (node: TaxonomyDraftNode): void => {
        setEditingId(node.draftId);
        setEditValue(node.label);
    };
    const commitEdit = (node: TaxonomyDraftNode): void => {
        const next = editValue.trim();
        if (next.length > 0 && next !== node.label) vm.renameNode(node.draftId, next);
        setEditingId(null);
    };
    const drop = (targetDraftId: string | null): void => {
        if (dragId !== null) vm.reparentNode(dragId, targetDraftId);
        setDragId(null);
        setOverId(null);
        setRootOver(false);
    };

    const filter = vm.filterText.trim().toLowerCase();
    const matches = (n: TaxonomyDraftNode): boolean => n.label.toLowerCase().includes(filter) || n.children.some(matches);
    const roots = filter.length > 0 ? vm.categories.filter(matches) : vm.categories;

    const renderNode = (node: TaxonomyDraftNode, depth: number): ReactElement => {
        const kids = filter.length > 0 ? node.children.filter(matches) : node.children;
        const hasChildren = kids.length > 0;
        const expanded = filter.length > 0 ? true : vm.isExpanded(node.draftId);
        const editing = editingId === node.draftId;
        const isDropTarget = overId === node.draftId && dragId !== null && dragId !== node.draftId;
        return (
            <li key={node.draftId}>
                <Box style={{ position: 'relative' }}>
                    <Paper
                        className="bm-org-card"
                        withBorder
                        radius="md"
                        p="xs"
                        draggable={!editing}
                        onDragStart={(e): void => {
                            e.stopPropagation();
                            setDragId(node.draftId);
                        }}
                        onDragEnd={(): void => {
                            setDragId(null);
                            setOverId(null);
                            setRootOver(false);
                        }}
                        onDragOver={(e): void => {
                            if (dragId === null || dragId === node.draftId) return;
                            e.preventDefault();
                            setOverId(node.draftId);
                        }}
                        onDragLeave={(): void => setOverId((o) => (o === node.draftId ? null : o))}
                        onDrop={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                            drop(node.draftId);
                        }}
                        style={{
                            width: 200,
                            opacity: node.enabled ? 1 : 0.5,
                            outline: isDropTarget ? '2px solid var(--mantine-primary-color-filled)' : 'none',
                            outlineOffset: 2,
                            cursor: editing ? 'text' : 'grab',
                        }}
                    >
                        <Stack gap={5} align="center">
                            {node.status === 'edited' && (
                                <Group gap={5} justify="center">
                                    <Badge size="xs" variant="light" color="yellow">
                                        edited
                                    </Badge>
                                </Group>
                            )}
                            {editing ? (
                                <TextInput
                                    size="xs"
                                    value={editValue}
                                    data-autofocus
                                    autoFocus
                                    onChange={(e): void => setEditValue(e.currentTarget.value)}
                                    onBlur={(): void => commitEdit(node)}
                                    onKeyDown={(e): void => {
                                        if (e.key === 'Enter') commitEdit(node);
                                        if (e.key === 'Escape') setEditingId(null);
                                    }}
                                />
                            ) : (
                                <Text fw={600} size="sm" ta="center" lineClamp={2} onDoubleClick={(): void => beginEdit(node)} style={{ cursor: 'text' }}>
                                    {node.label}
                                </Text>
                            )}
                            <Group gap={2} justify="center">
                                <Tooltip label={node.enabled ? 'Enabled for classification' : 'Disabled'} withArrow>
                                    <Switch
                                        size="xs"
                                        checked={node.enabled}
                                        onChange={(): void => vm.toggleEnabled(node.draftId)}
                                        aria-label={`Toggle ${node.label}`}
                                    />
                                </Tooltip>
                                {canAddBeneathDepth(depth) && (
                                    <Tooltip label="Suggest with AI" withArrow>
                                        <ActionIcon size="sm" variant="subtle" color="indigo" aria-label="Suggest" onClick={(): void => onAssist(node.draftId)}>
                                            <Sparkles size={13} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                {canAddBeneathDepth(depth) && (
                                    <Tooltip label="Add child" withArrow>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            aria-label="Add child"
                                            onClick={(): void => {
                                                const id = vm.addSubcategory(node.draftId, 'New category');
                                                if (id !== null) {
                                                    setEditingId(id);
                                                    setEditValue('New category');
                                                }
                                            }}
                                        >
                                            <Plus size={13} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                <Tooltip label="Remove" withArrow>
                                    <ActionIcon size="sm" variant="subtle" color="red" aria-label="Remove" onClick={(): void => vm.removeNode(node.draftId)}>
                                        <X size={13} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Stack>
                    </Paper>

                    {hasChildren && (
                        <ActionIcon
                            variant="default"
                            radius="xl"
                            size="sm"
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            onClick={(): void => vm.toggleExpand(node.draftId)}
                            style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translate(-50%, -3px)', zIndex: 2 }}
                        >
                            {expanded ? <Minus size={14} /> : <Plus size={14} />}
                        </ActionIcon>
                    )}
                </Box>
                {hasChildren && expanded && <ul>{kids.map((k) => renderNode(k, depth + 1))}</ul>}
            </li>
        );
    };

    return (
        <Stack gap="xs">
            {dragId !== null && (
                <Paper
                    withBorder
                    radius="md"
                    p="xs"
                    onDragOver={(e): void => {
                        e.preventDefault();
                        setRootOver(true);
                    }}
                    onDragLeave={(): void => setRootOver(false)}
                    onDrop={(e): void => {
                        e.preventDefault();
                        drop(null);
                    }}
                    style={{
                        borderStyle: 'dashed',
                        textAlign: 'center',
                        color: 'var(--mantine-color-dimmed)',
                        background: rootOver ? 'var(--mantine-color-default-hover)' : 'transparent',
                    }}
                >
                    <Group gap={6} justify="center">
                        <CornerUpLeft size={14} />
                        <Text size="sm">Drop here to make it a top-level category</Text>
                    </Group>
                </Paper>
            )}
            <Card withBorder radius="md" p="md" style={{ overflowX: 'auto' }}>
                {vm.categories.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                        No categories yet — pick a grouping and hit Generate, or add one manually.
                    </Text>
                ) : (
                    <Box style={{ textAlign: 'center', minWidth: 'min-content' }}>
                        <div className="bm-org">
                            <ul>{roots.map((n) => renderNode(n, 0))}</ul>
                        </div>
                    </Box>
                )}
            </Card>
        </Stack>
    );
}
